import {Component, EventEmitter, HostListener, Injector, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NotificationService} from '../../../../../../services/NotificationService';
import {ActivatedRoute, Router} from '@angular/router';
import {
    AssistanceApplication,
    AssistanceApplicationClient,
    AssistanceApplicationFileClient,
    AssistanceApplicationType,
    AuditType,
    Client,
    Company, CompanyCategory,
    CompanyCategoryThresholdViewModel,
    CompanyCategoryType,
    CompanyPayeeType,
    CreateAssistanceApplication,
    FundTypeClient,
    LookUp,
    PledgeType,
    PublicClient,
    UpdateAssistanceApplication,
    User,
    UserType,
} from '../../../../../../services/api.client';
import {SecurityService} from '../../../../../../services/SecurityService';
import {BehaviorSubject, firstValueFrom, forkJoin, lastValueFrom, Observable, of, Subscription} from 'rxjs';
import {Masking} from '../../../../../../utilities/Masking';
import {PromptComponent} from '../../../../../shared/dialogs/PromptComponent';
import {extractEnumNames, getMessageFromException} from '../../../../../../utilities/Util';
import {CompanyListModalComponent} from '../../../../company/CompanyListModalComponent';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Validators} from '../../../../../../utilities/Validators';
import {Disposable} from '../../../../../../utilities/Disposable';
import {CompanyCategoryService} from '../../../../../../services/companyCategory.service';
import {requiredDocumentsUploadedValidator} from '../../../../../shared/assistance-application-document-upload/documentValidator';
import {
    ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS
} from '../../../../../../shared/constants/options/assistance-application-denial-options';
import {AssistanceApplicationStatus} from '../../../../../../shared/enums/assistance-application-status';
import {AssistanceApplicationDenialReason} from '../../../../../../shared/enums/assistance-application-denial-reason';
import {requireDenialReasonIfDenied} from './validators';
import {CompanyService} from '../../../../../../services/CompanyService';
import swal from 'sweetalert2';
import Swal from 'sweetalert2';
import {CurrencyPipe} from '@angular/common';
import {FundAdministrativeAgencyNameMap} from '../../../../../../shared/constants/FundConstants';
import {IPayeeType} from '../../../../../../shared/interfaces/IPayeeType';
import {ICategoryThreshold} from '../../../../../../shared/interfaces/ICategoryThreshold';
import {IPayeeCompanyCategoryAmount} from '../../../../../../shared/interfaces/IPayeeCompanyCategoryAmount';
import {CompanySelectedEvent} from '../../../../../shared/events/company-selected-event';
import {SwalComponent} from '@sweetalert2/ngx-sweetalert2';
import {fileErrorNames, formControlErrorMapper} from './form-control-error-mapper';
import {AssistanceApplicationService} from '../../../../../public/application/assistance-application.service';
import {IncomeValidatorService} from '../../../../../../shared/utils/income-validator.service';
import {AnnualIncomeService} from '../../../../../../services/AnnualIncomeService';
import {TierLevelOptions, TierStandardLimitMap} from '../constants/TierLevels';
import {AssistanceApplicationAuditType} from '../../../../../../shared/enums/assistance-application-audit-type';
import {InternalDraftService} from '../../../../../../services/internal-draft.service';
import {DraftApplicationFile, DraftApplicationFileClient, InternalDraftFormData, SaveInternalDraftRequest, CompanyAddressChangeRequestViewModel} from '../../../../../../services/api.client';
import {CompanyAddressChangeRequestService} from '../../../../../../services/companyAddressChangeRequest.service';

@Component({
    selector: 'app-assistance-application-form',
    templateUrl: './assistance-application-form.component.html',
    styleUrls: ['./assistance-application-form.component.scss'],
    standalone: false
})
export class AssistanceApplicationFormComponent extends Disposable implements OnInit {
    private _isBirthYearAndSsnOptional = false;

    constructor(private formBuilder: UntypedFormBuilder,
                private assistanceApplicationClient: AssistanceApplicationClient,
                private router: Router,
                private fundTypeClient: FundTypeClient,
                private injector: Injector,
                private activatedRoute: ActivatedRoute,
                private _companyService: CompanyService,
                private _companyCategoryService: CompanyCategoryService,
                private _assistanceApplicationService: AssistanceApplicationService,
                private assistanceApplicationFileClient: AssistanceApplicationFileClient,
                private incomeValidator: IncomeValidatorService,
                private currencyPipe: CurrencyPipe,
                private annualIncomeService: AnnualIncomeService,
                private publicClient: PublicClient,
                private internalDraftService: InternalDraftService,
                private draftApplicationFileClient: DraftApplicationFileClient,
                private _companyAddressChangeRequestService: CompanyAddressChangeRequestService,
                private _securityService: SecurityService) {
        super();
        this.states$ = this._securityService.states$;
        this.categoryThresholds$ = this._companyCategoryService.categoryThresholds$;
        this.loggedInUser$ = this._securityService.currentUser$;
    }

    get isBirthYearAndSsnOptional(): boolean {
        return this._isBirthYearAndSsnOptional || (this.assistanceApplication?.fundType?.isBirthYearAndSsnOptional ?? false);
    }

    get isAuditRequired(): boolean {
        return this.assistanceApplication?.fundType?.auditType.toString() === AssistanceApplicationAuditType.AgencyReview ||
            this.assistanceApplication?.fundType?.auditType.toString() === AssistanceApplicationAuditType.ReviewAndAudit;
    }

    get assistanceApplicationAreasOfNeedGroup(): FormGroup {
        return this.form.get('assistanceApplicationAreasOfNeed') as FormGroup;
    }

    get fundPledgeTypeId(): FormControl {
        return this.form.get('fundPledgeTypeId') as FormControl;
    }

    get totalNeedControl(): FormControl {
        return this.form.get('totalNeed') as FormControl;
    }

    get femaRegistrationNumberControl(): FormControl {
        return this.form.get('stormApplication').get('femaRegistrationNumber') as FormControl;
    }

    get visionLinkNumberControl(): FormControl {
        return this.form.get('stormApplication').get('visionLinkNumber') as FormControl;
    }

    get tierLevelControl(): FormControl {
        return this.form.get('stormApplication').get('tierLevel') as FormControl;
    }

    get longTermRecoveryApprovedControl(): FormControl {
        return this.form.get('stormApplication').get('longTermRecoveryApproved') as FormControl;
    }

    get assignedAgencyIdControl(): FormControl {
        return this.form.get('assignedAgencyId') as FormControl;
    }

    get summaryControl(): FormControl {
        return this.form.get('summary') as FormControl;
    }

    get descriptionControl(): FormControl {
        return this.form.get('description') as FormControl;
    }

    get censusTractControl(): FormControl {
        return this.form.get('censusTract') as FormControl;
    }

    get statusControl(): FormControl {
        return this.form.get('status') as FormControl;
    }

    get denialReasonControl(): FormControl<AssistanceApplicationDenialReason> {
        return this.form.get('denialReason') as FormControl<AssistanceApplicationDenialReason>;
    }

    get otherDenialReasonControl(): FormControl<string> {
        return this.form.get('otherDenialReason') as FormControl<string>;
    }

    get companiesFormArray(): FormArray {
        return this.form.get('companies') as FormArray;
    }

    get denied(): boolean {
        return this.statusControl.value === AssistanceApplicationStatus.Denied;
    }

    get otherDenialReasonSelected(): boolean {
        return this.denialReasonControl.value === AssistanceApplicationDenialReason.Other;
    }

    get filesFormArray(): FormArray {
        return this.form?.get('files') as FormArray;
    }

    get hasDifferentDamagedDwellingAddressControl(): FormControl {
        return this.form.get('hasDifferentDamagedDwellingAddress') as FormControl;
    }

    get stormApplicationGroup(): FormGroup {
        return this.form.get('stormApplication') as FormGroup;
    }

    get hasDifferentDamagedDwellingAddress(): boolean {
        return this.hasDifferentDamagedDwellingAddressControl?.value === true;
    }

    get hasApplicantDifferences(): boolean {
        return this.hasDifferentIndividualIncome || this.hasDifferentCounty || this.hasDifferentEmail || this.hasDifferentHouseholdIncome;
    }

    get hasDifferentEmail(): boolean {
        return this.assistanceApplication.applicantEmail.toLowerCase() !== this.client?.email?.toLowerCase();
    }

    get hasDifferentCounty(): boolean {
        return this.assistanceApplication.applicantCounty?.toLowerCase() !== this.client.county?.toLowerCase();
    }

    get hasDifferentHouseholdIncome(): boolean {
        return Math.trunc(this.assistanceApplication.applicantHouseHoldIncome) !== Math.trunc(this.client.houseHoldIncome);
    }

    get hasDifferentIndividualIncome(): boolean {
        return Math.trunc(this.assistanceApplication.applicantIndividualMonthlyIncome) !== Math.trunc(this.client.individualMonthlyIncome);
    }

    get allowThresholdOverride(): boolean {
        return this.user?.userType === UserType.SystemAdmin || this.user?.userType === UserType.RegionAdmin;
    }

    get shouldBeDisabled(): boolean {
        return (this.assistanceApplication?.fundType?.auditType.toString() === AssistanceApplicationAuditType.ReviewAndAudit
                || this.assistanceApplication?.fundType?.auditType.toString() === AssistanceApplicationAuditType.AgencyReview)
            && this.disabledState.includes(this.assistanceApplication?.status);
    }

    get shouldRequireMinors(): boolean {
        return this.applicationType !== AssistanceApplicationType.STLWater
            && this.applicationType !== AssistanceApplicationType.STLStorm;
    }

    get familyEducatedAboutResourcesControl(): FormControl {
        return this.form.get('familyEducatedAboutResources') as FormControl;
    }

    get shouldAllowPayeeEdits(): boolean {
        return this.applicationType !== AssistanceApplicationType.STLWater;
    }

    public get swapTranslationText(): string {
        return this.showOriginalDescription ? 'Click to view English' : 'Click to view Spanish';
    }

    public get hasIncomeValidationError(): boolean {
        const hasError = this.form.hasError('incomeExceedsLimit');
        return hasError || this.incomeExceedsLimit();
    }

    @ViewChild('auditModal') auditModal: PromptComponent;

    @ViewChild('exceedsCategoryThreshold') exceedsCategoryThresholdModal: PromptComponent;

    @ViewChild('loadingDialog')
    public readonly loadingDialog!: SwalComponent;
    @ViewChild(PromptComponent)
    clientIsInactiveModal: PromptComponent;
    @ViewChild(CompanyListModalComponent) companyListModal: CompanyListModalComponent;
    @ViewChild('addressEditModal') addressEditModal: PromptComponent;
    editingAddressCompany: AbstractControl | null = null;
    private addressSnapshot: { address: string, address2: string, city: string, state: string, zip: string } | null = null;
    updateMasterAddress = false;
    pendingAddressChangeRequests: CompanyAddressChangeRequestViewModel[] = [];

    @Input()
    client: Client = null;

    @Input() user: User;

    @Input() assistanceApplication: AssistanceApplication;

    @Input() auditState: string;
    @Input() applicationLoading = true;
    @Input() defaultOpenDocumentUpload = true;
    @Input() isNewApplication: boolean;

    @Input() applicationType: AssistanceApplicationType;
    @Input() hasTranslation: boolean;
    draftId: string | null = null;
    @Input() canUploadDocuments = false;
    @Input() formValuesSet = false;

    @Output() onUpdate = new EventEmitter<AssistanceApplication>();

    loggedInUser: User;
    loggedInUser$: Observable<User>;
    softDeletedIds = [];
    softDeletedDraftFileIds: string[] = [];

    public ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS = ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS;
    public form: UntypedFormGroup;

    public companyFormArrayMetaData: { ignoreDuplicates: boolean, duplicateCompany: undefined | null | Company }[] = [];

    maxIncomeLevel$ = new BehaviorSubject<number>(0);
    hasHouseholdMismatch$ = new BehaviorSubject<boolean>(false);
    loadingSubject = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject.asObservable();
    saving$ = new BehaviorSubject<boolean>(false);
    savingMessage$ = new BehaviorSubject<string>('');
    savingTitle$ = new BehaviorSubject<string>('');
    percentComplete$ = new BehaviorSubject<string>('50%');
    formErrors$ = new BehaviorSubject<string[]>([]);

    public uploadedCount = 0;
    public totalToUpload = 0;
    states: LookUp[];
    states$: Observable<LookUp[]>;
    fundTypes: PledgeType[];

    categoryThresholds: CompanyCategoryThresholdViewModel[];
    categoryThresholds$: Observable<CompanyCategoryThresholdViewModel[]>;
    categoryThresholdsSelect: ICategoryThreshold[];
    categories$ = new BehaviorSubject<CompanyCategory[]>([]);
    categories: CompanyCategory[] = [];

    payeeTypesSelect: IPayeeType[];

    disabledState = [
        AssistanceApplicationStatus.Approved.toString(),
        AssistanceApplicationStatus.UnderReview.toString(),
        AssistanceApplicationStatus.ReviewPassed.toString(),
        AssistanceApplicationStatus.Denied.toString()
    ];

    utilityTotal = 0;
    rentalTotal = 0;
    transportationTotal = 0;
    childcareTotal = 0;
    carRepairTotal = 0;

    assistanceOptions: LookUp[];
    assistanceOptions$: Observable<LookUp[]>;

    zipCodeMask = Masking.zipMask;
    isCountyActive: boolean;
    clientCountyInactiveMessage: string;

    public householdIncomeAlertMessage: string;
    public individualIncomeAlertMessage: string;
    public emailAlertMessage: string;
    public countyAlertMessageHtml: string;
    public countyAlertMessageText: string;
    public exceedTierMessageText: string;
    public showBulkAlerts = true;
    public showOriginalDescription = true;
    public stormTotalRequestedToDate = 0;
    assistanceApplicationStates = extractEnumNames(AssistanceApplicationStatus);
    public tierLevelOptions = TierLevelOptions;
    date = new Date();
    householdSize = 0;

    protected readonly AssistanceApplicationDenialReason = AssistanceApplicationDenialReason;
    protected readonly AssistanceApplicationType = AssistanceApplicationType;

    private selectedCompanies: Company[] = [];
    private readonly tierStandardLimitMap = TierStandardLimitMap;
    private payeesSub?: Subscription;
    private administrativeAgencyFundTypeId: string;
    private _originalEditCase = new BehaviorSubject<AssistanceApplication>(null);

    // End storm specific messages
    protected readonly AuditType = AuditType;
    protected readonly AssistanceApplicationAuditType = AssistanceApplicationAuditType;

    /** Convert empty-string birthYear/ssn to null so the API receives valid nullable values. */
    private sanitizeHouseholdMembers(members: any[]): any[] {
        if (!members) { return members; }
        return members.map(m => ({
            ...m,
            birthYear: m.birthYear === '' ? null : m.birthYear,
            ssn: m.ssn === '' ? null : m.ssn,
        }));
    }

    ngOnInit(): void {
        if (!this.applicationType && this.assistanceApplication) {
            const raw = this.assistanceApplication.applicationType;
            this.applicationType =
                typeof raw === 'number'
                    ? (raw as AssistanceApplicationType)
                    : AssistanceApplicationType[raw as keyof typeof AssistanceApplicationType];
        }

        switch (this.applicationType) {
            case AssistanceApplicationType.UHONS:
                this.assistanceOptions$ = this._securityService.administrativeAgencyFundAssistanceUhons$;
                break;
            case AssistanceApplicationType.UFF:
                this.assistanceOptions$ = this._securityService.administrativeAgencyFundAssistanceUff$;
                break;
            case AssistanceApplicationType.STLWater:
                this.assistanceOptions$ = this._securityService.administrativeAgencyFundAssistanceSTLWater$;
                break;
            case AssistanceApplicationType.STLStorm:
                this.assistanceOptions$ = this._securityService.administrativeAgencyFundAssistanceStorm$;
                break;
            default:
                this.assistanceOptions$ = of([]);
                break;
        }

        if (this.applicationType !== undefined && this.applicationType !== null) {
            this.publicClient.getFundTypeConfiguration(this.applicationType)
                .pipe(takeUntil(this.$destroyed))
                .subscribe({
                    next: (fundType) => {
                        this._isBirthYearAndSsnOptional = fundType?.isBirthYearAndSsnOptional ?? false;
                    },
                    error: () => {
                        this._isBirthYearAndSsnOptional = false;
                    }
                });
        }

        this.states$.subscribe(states => this.states = states);
        this.assistanceOptions$.subscribe(options => {
            this.assistanceOptions = options;
        });
        this.initForm();
        this.setPayeeTypesSelect();
        this.categoryThresholds$.subscribe(categoryThresholds => {
            this.categoryThresholds = categoryThresholds;
            this.categories = categoryThresholds.map(x => {
                return CompanyCategory.fromJS({
                    category: x.category,
                    categoryLabel: x.categoryLabel
                });
            });
            // Emit the new categories to the observable
            this.categories$.next(this.categories);
            this.setCategoryThresholdsSelect();
        });
        this.assignedAgencyIdControl.setValue(this.user?.agencyId);
        this.assistanceApplicationClient.isAssistanceApplicationCountyActive(this.client?.county, this.applicationType)
            .pipe(takeUntil(this.$destroyed), distinctUntilChanged())
            .subscribe(isActive => this.isCountyActive = isActive);
        const fundTypeAgencyName = FundAdministrativeAgencyNameMap[this.applicationType];
        this.fundTypeClient.getFundTypeId(fundTypeAgencyName).subscribe(x => {
            this.administrativeAgencyFundTypeId = x;
        });

        this.loggedInUser$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(loggedInUser => {
                this.loggedInUser = loggedInUser;
                if (!this.user?.agencyId) {
                    this.handleFundPledgeTypeIds(loggedInUser?.agencyId);
                }
            });

        if (this.user?.agencyId) {
            this.handleFundPledgeTypeIds(this.user?.agencyId);
        }
        this.form.valueChanges.pipe(takeUntil(this.$destroyed)).subscribe(() => {
            if (!this.denied) {
                this.denialReasonControl.reset({value: null, disabled: true}, {emitEvent: false});
            } else {
                this.denialReasonControl.enable({emitEvent: false});
            }

            if (this.otherDenialReasonSelected) {
                this.otherDenialReasonControl.setValidators(Validators.required);
            } else {
                this.otherDenialReasonControl.clearValidators();
                this.otherDenialReasonControl.reset(null, {emitEvent: false});
            }
        });

        this.checkForDuplicateCompanyOnFormChanges();
        this.setAlertMessages();
        this.configureStormFieldValidators();

        switch (this.applicationType) {
            case AssistanceApplicationType.UHONS:
                break;
            case AssistanceApplicationType.UFF:
                break;
            case AssistanceApplicationType.STLWater:
                this.preFillStlWaterApplication();
                break;
            case AssistanceApplicationType.STLStorm:
                if (!this.assistanceApplication?.stormApplication?.totalAmountRequestedToDate) {
                    this.assistanceApplicationClient.getStormTotalNeedRequested(this.client.id)
                        .subscribe(totalRequested => {
                            this.stormTotalRequestedToDate = totalRequested;
                        });
                } else {
                    this.stormTotalRequestedToDate = this.assistanceApplication.stormApplication.totalAmountRequestedToDate;
                }

                if (!this.shouldBeDisabled) {
                    this.tierLevelControl?.valueChanges
                        .pipe(distinctUntilChanged())
                        .subscribe(() => {
                            this.checkTierExceed().then();
                        });

                    if (this.companiesFormArray) {
                        this.payeesSub = this.companiesFormArray.valueChanges.pipe(
                            map(() => this.getTotalPayeeAmountFromFormControls()),
                            debounceTime(1000),
                            distinctUntilChanged()
                        ).subscribe(() => {
                            this.checkTierExceed().then();
                        });
                    }
                } else {
                    this.exceedTierMessageText = this.getTierMessage();
                }
                break;
            default:
                break;
        }

        this.saving$.pipe(takeUntil(this.$destroyed), distinctUntilChanged()).subscribe(val => {
            if (val) {
                this.loadingDialog?.fire().then();
            } else {
                setTimeout(() => {
                    this.loadingDialog?.close().then();
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.payeesSub?.unsubscribe();
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            clientId: ['', Validators.required],
            fundPledgeTypeId: ['', Validators.required],
            totalNeed: ['', Validators.compose([Validators.required])],
            assignedAgencyId: [null, Validators.required],
            summary: ['', Validators.compose([Validators.required, Validators.maxLength(128)])],
            description: ['', Validators.compose([Validators.required, Validators.maxLength(2048)])],
            translatedDescription: '',
            status: new FormControl<AssistanceApplicationStatus | ''>('', Validators.required),
            censusTract: this.formBuilder.control('', []),
            denialReason: new FormControl<AssistanceApplicationDenialReason | string>(''),
            otherDenialReason: new FormControl<string>('', Validators.maxLength(128)),
            fundedDate: '',
            companies: this.formBuilder.array([this.initCompanyForm()]),
            adults: this.formBuilder.array([]),
            minors: this.formBuilder.array([]),
            assistanceApplicationAreasOfNeed: this.buildAreasOfNeedGroup(),
            files: this.formBuilder.array([]),
            familyEducatedAboutResources: new FormControl(null),
            hasDifferentDamagedDwellingAddress: [false],
            stormApplication: this.formBuilder.group({
                id: null,
                assistanceApplicationId: null,
                damagedDwellingAddress1: [''],
                damagedDwellingAddress2: [''],
                damagedDwellingCity: [''],
                damagedDwellingState: [''],
                damagedDwellingZipCode: [''],
                tierLevel: [''],
                femaRegistrationNumber: [''],
                visionLinkNumber: [''],
                longTermRecoveryApproved: [false]
            }),
        }, {
            validators: [requireDenialReasonIfDenied(),
                requiredDocumentsUploadedValidator(this.client, this.applicationType),
                this.incomeValidator.internalApplicationIncomeValidator(this.applicationType, this.client)]
        });

        const adultsFA = this.form.get('adults') as FormArray;
        const minorsFA = this.form.get('minors') as FormArray;
        [adultsFA, minorsFA].forEach(fa =>
            fa.valueChanges
                .pipe(takeUntil(this.$destroyed))
                .subscribe(() => {
                    this.form.updateValueAndValidity();
                    if (this.formValuesSet) {
                        this.validateHouseHoldSize();
                    }
                    })
        );

        // Reset evictionNotification when parent rent checkbox is unchecked
        ['uhonsRent', 'uffRentPayment'].forEach(controlName => {
            this.assistanceApplicationAreasOfNeedGroup.get(controlName)?.valueChanges
                .pipe(takeUntil(this.$destroyed))
                .subscribe(checked => {
                    if (!checked) {
                        this.assistanceApplicationAreasOfNeedGroup.get('evictionNotification')?.setValue(false);
                    }
                });
        });

        // Add validation logic for damaged dwelling address
        this.hasDifferentDamagedDwellingAddressControl.valueChanges
            .pipe(takeUntil(this.$destroyed))
            .subscribe(hasDifferentAddress => {
                this.updateDamagedDwellingAddressValidation(hasDifferentAddress);
            });
    }

    private initCompanyForm(): FormGroup {
        return this.formBuilder.group({
            useNewCompany: new FormControl(false),
            paymentTo: [''],
            payeeAddress: [''],
            payeeAddress2: [''],
            accountNumber: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            payeeCity: [''],
            payeeState: [''],
            payeeZip: [''],
            payeeType: [null],
            id: [null, Validators.requiredWhen('useNewCompany', false)],
            companyId: [null],
            companyName: [null, Validators.required],
            isLegacyPayee: [false],
            amount: new FormControl<number | null>(null, Validators.required),
            category: ['', Validators.required],
            pledgeId: [null]
        }, {
            validators: Validators.compose([
                Validators.mutuallyExclusive('companyId', 'paymentTo', 'Company', 'Payment To'),
            ])
        });
    }

    private getProgramMaximumHouseholdIncome(): number {

        const adultControl = this.form.get('adults');
        const minorControl = this.form.get('minors');

        const householdCount = (adultControl?.value?.length ?? 0) + (minorControl.value?.length ?? 0) + 1;
        this.householdSize = householdCount;

        let maxIncome = 0;
        switch (this.applicationType) {
            case AssistanceApplicationType.STLWater:
                maxIncome = this.annualIncomeService.calculatePovertyLevelMaxAnnualStlWaterIncomeForHouseholdCount(householdCount);
                break;
            case AssistanceApplicationType.UHONS:
                maxIncome = this.annualIncomeService.calculatePovertyLevelMaxAnnualUhonsIncomeForHouseholdCount(householdCount);
                break;
            case AssistanceApplicationType.UFF:
                maxIncome = this.annualIncomeService.calculatePovertyLevelMaxAnnualUFFIncomeForHouseholdCount(householdCount);
                break;
            default:
                return null;
        }
        return maxIncome / 12; // Return monthly income
    }

    private updateDamagedDwellingAddressValidation(hasDifferentAddress: boolean): void {
        const stormGroup = this.stormApplicationGroup;

        if (hasDifferentAddress && this.applicationType === AssistanceApplicationType.STLStorm) {
            // Add required validators

            stormGroup.get('damagedDwellingAddress1')?.setValidators([Validators.required]);
            stormGroup.get('damagedDwellingCity')?.setValidators([Validators.required]);
            stormGroup.get('damagedDwellingState')?.setValidators([Validators.required]);
            stormGroup.get('damagedDwellingZipCode')?.setValidators([Validators.required]);
        } else {
            if (!hasDifferentAddress) {
                stormGroup.get('damagedDwellingAddress1')?.setValidators([]);
                stormGroup.get('damagedDwellingCity')?.setValidators([]);
                stormGroup.get('damagedDwellingState')?.setValidators([]);
                stormGroup.get('damagedDwellingZipCode')?.setValidators([]);
            }
        }

        // Update validity
        stormGroup.get('damagedDwellingAddress1')?.updateValueAndValidity();
        stormGroup.get('damagedDwellingAddress2')?.updateValueAndValidity();
        stormGroup.get('damagedDwellingCity')?.updateValueAndValidity();
        stormGroup.get('damagedDwellingState')?.updateValueAndValidity();
        stormGroup.get('damagedDwellingZipCode')?.updateValueAndValidity();
    }

    private configureStormFieldValidators(): void {
        const stormGroup = this.stormApplicationGroup;

        if (!stormGroup) {
            return;
        }

        const stormRequiredControls = [
            'tierLevel',
            'visionLinkNumber',
        ];

        stormRequiredControls.forEach(name => {
            const control = stormGroup.get(name);
            if (!control) {
                return;
            }

            if (this.applicationType === AssistanceApplicationType.STLStorm) {
                control.setValidators([Validators.required]);
            } else {
                control.clearValidators();
            }

            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    private buildAreasOfNeedGroup(): FormGroup {
        const controlsConfig: { [key: string]: any } = {};
        (this.assistanceOptions || []).forEach(opt => {
            controlsConfig[opt.key] = [false];
        });
        controlsConfig.evictionNotification = [false];
        return this.formBuilder.group(controlsConfig, {validators: [Validators.atLeastOneChecked()]});
    }

    private setPercentComplete(numDone: number, total: number): void {
        const val = `${Math.floor((numDone / total) * 100)}%`;
        this.percentComplete$.next(val);
    }

    private preFillStlWaterApplication(): void {
        this.companiesFormArray.controls[0].get('companyName').disable();
        this.companiesFormArray.controls[0].get('category').disable();
        this.companiesFormArray.controls[0].get('useNewCompany').disable();
        this.censusTractControl.disable();

        this.assistanceApplicationAreasOfNeedGroup.get('stlWaterWater').setValue(true);
        this.assistanceApplicationAreasOfNeedGroup.get('stlWaterWater').disable();

        this._assistanceApplicationService.getProgramDefaultPayee(this.applicationType).subscribe(company => {
            this.companiesFormArray.controls[0].patchValue({
                companyId: company.id,
                companyName: company.name,
                category: company.category,
                payeeAddress: company.address1,
                payeeAddress2: company.address2,
                payeeCity: company.city,
                payeeState: company.state,
                payeeZip: company.zipCode,
            });
        });
    }

    private checkForDuplicateCompanyOnFormChanges(): void {
        this.companiesFormArray.valueChanges.pipe(takeUntil(this.$destroyed), debounceTime(3000)).subscribe(() => {
            this.companiesFormArray.controls.forEach((companyFormGroup, i) => {
                const isNewCompany = companyFormGroup.get('useNewCompany').value;
                if (isNewCompany) {
                    const name = companyFormGroup.get('paymentTo').value;
                    const address = companyFormGroup.get('payeeAddress').value;

                    if (!name && !address) {
                        return;
                    }

                    this._companyService.checkDuplicateCompany(name, address).subscribe(next => {
                        const existingCompanyFound = !!next.company;
                        if (existingCompanyFound) {
                            const metaData = this.companyFormArrayMetaData[i];
                            if (!metaData.ignoreDuplicates) {
                                metaData.duplicateCompany = next.company;
                            }
                        }
                    });
                }
            });
        });
    }

    private isValidForm(): boolean {
        if (!this.isCountyActive) {
            this.clientCountyInactiveMessage = `The Client County ${this.client?.county} is inactive and is not currently accepting new cases.`;
            // Informational modal only: user acknowledges and stays on the page.
            this.clientIsInactiveModal.open().pipe(takeUntil(this.$destroyed)).subscribe();
            return false;
        }

        this.form.updateValueAndValidity();
        if (!this.form.valid && !(this.statusControl.value === AssistanceApplicationStatus.Denied)) {
            this.injector.get(NotificationService).showError('Please fill out all required fields and ensure all necessary documents are uploaded.');
            this.form.markAllAsTouched();
            return false;
        }

        if (!this.isAssistanceRequestTypeValid()) {
            return false;
        }

        const isIncomeValid = this.validateIncome();
        if (!isIncomeValid && !(this.statusControl.value === AssistanceApplicationStatus.Denied)) {
            this.injector.get(NotificationService).showError('Individual Monthly Incomes Do Not Equal Total Household Monthly Income.');
            return false;
        }

        return true;
    }

    private isAssistanceRequestTypeValid(): boolean {
        const assistanceRequestDescription = this.assistanceApplicationAreasOfNeedGroup as FormGroup;

        // Use getRawValue() so disabled controls are included. For STLWater the only
        // area of need (stlWaterWater) is pre-checked and disabled, and FormGroup.value
        // omits disabled controls — which would otherwise make this validation fail.
        const assistanceValues: boolean[] = Object.values(assistanceRequestDescription.getRawValue());

        if (!assistanceValues.includes(true)) {
            this.injector.get(NotificationService).showError('Please Select At Least One Assistance Area.');
            return false;
        }

        return true;
    }

    private validateIncome(): boolean {
        let householdMonthlyIncome: number;
        let applicantMonthlyIncome: number;

        if (this.assistanceApplication && this.assistanceApplication.isPublic) {
            // if from a public application, use the applicant's income from the public application
            householdMonthlyIncome = +this.assistanceApplication?.applicantHouseHoldIncome;
            applicantMonthlyIncome = +this.assistanceApplication?.applicantIndividualMonthlyIncome;
        } else {
            // if an internal application, use the income entered from the client record
            householdMonthlyIncome = +this.client?.houseHoldIncome;
            applicantMonthlyIncome = +this.client?.individualMonthlyIncome;
        }

        let totalIndividualIncomes = 0;

        const adultsEntered = this.form.get('adults').value;
        adultsEntered.forEach((x: { monthlyIncomeAmount: string | number; }) => {
            const incomeAmount = +x.monthlyIncomeAmount;
            totalIndividualIncomes += incomeAmount;
        });

        totalIndividualIncomes += +applicantMonthlyIncome;

        return +householdMonthlyIncome === totalIndividualIncomes;
    }

    private setCategoryThresholdsSelect(): void {
        this.categoryThresholdsSelect = this.categoryThresholds.map(x => ({
            key: x.category.toString(),
            value: x.categoryLabel
        } as ICategoryThreshold));
    }

    private setPayeeTypesSelect(): void {
        this.payeeTypesSelect = ([{
            key: CompanyPayeeType.Company,
            value: 'Company'
        }, {
            key: CompanyPayeeType.Individual,
            value: 'Individual'
        }] as IPayeeType[]);
    }

    private doesCategoryAmountsHaveValidAmounts(): boolean {
        if (this.applicationType === AssistanceApplicationType.STLStorm) {
            return true;
        }
        this.rentalTotal = 0;
        this.utilityTotal = 0;
        this.transportationTotal = 0;
        this.childcareTotal = 0;
        this.carRepairTotal = 0;

        const companies = this.companiesFormArray.getRawValue();

        const utility = CompanyCategoryType.Utility;
        const rental = CompanyCategoryType.EmergencyRentalAssistance;
        const transportation = CompanyCategoryType.TransportationForWorkforceNeeds;
        const childcare = CompanyCategoryType.ChildcareSupport;
        const carRepair = CompanyCategoryType.CarRepair;

        companies.map(company => {
            if (company?.category === rental) {
                this.rentalTotal += Number(company?.amount);
            } else if (company?.category === utility) {
                this.utilityTotal += Number(company?.amount);
            } else if (company?.category === transportation) {
                this.transportationTotal += Number(company?.amount);
            } else if (company?.category === childcare) {
                this.childcareTotal += Number(company?.amount);
            } else if (company?.category === carRepair) {
                this.carRepairTotal += Number(company?.amount);
            }
        });

        const utilityThreshold = this.categoryThresholds.find(x => x.category.toString() === utility);
        const rentalThreshold = this.categoryThresholds.find(x => x.category.toString() === rental);
        const transportationThreshold = this.categoryThresholds.find(x => x.category.toString() === transportation);
        const childcareThreshold = this.categoryThresholds.find(x => x.category.toString() === childcare);
        const carRepairThreshold = this.categoryThresholds.find(x => x.category.toString() === carRepair);

        const utilityThresholdAvailable = this.utilityTotal <= utilityThreshold?.thresholdAmount;
        const rentalThresholdAvailable = this.rentalTotal <= rentalThreshold?.thresholdAmount;
        const transportationThresholdAvailable = this.transportationTotal <= transportationThreshold?.thresholdAmount;
        const childcareThresholdAvailable = this.childcareTotal <= childcareThreshold?.thresholdAmount;
        const carRepairThresholdAvailable = this.carRepairTotal <= carRepairThreshold?.thresholdAmount;

        this.getPayeeCompanyCategoryAmounts(
            utilityThresholdAvailable,
            rentalThresholdAvailable,
            transportationThresholdAvailable,
            childcareThresholdAvailable,
            carRepairThresholdAvailable);

        return utilityThresholdAvailable &&
            rentalThresholdAvailable &&
            transportationThresholdAvailable &&
            childcareThresholdAvailable &&
            carRepairThresholdAvailable;
    }

    private showExceedsCategoryThresholdModal(): void {
        this.exceedsCategoryThresholdModal.open();
    }

    private handleFundPledgeTypeIds(agencyId: string): void {
        this.assistanceApplicationClient
            .getFundPledgeTypesForAgency(agencyId, this.applicationType, true, this.assistanceApplication?.fundPledgeTypeId)
            .pipe(takeUntil(this.$destroyed), distinctUntilChanged())
            .subscribe(fundTypes => {
                this.fundTypes = fundTypes;

                if (fundTypes?.length === 1) {
                    this.fundPledgeTypeId.setValue(fundTypes[0].id);
                    this.fundPledgeTypeId.disable();
                    this.fundPledgeTypeId.removeValidators(Validators.required);
                    this.fundPledgeTypeId.updateValueAndValidity({onlySelf: true});
                    return;
                }

                if (!fundTypes && this.assistanceApplication.fundPledgeTypeId) {
                    this.fundPledgeTypeId.setValue(this.assistanceApplication.fundPledgeTypeId);
                    this.fundPledgeTypeId.disable();
                    this.fundPledgeTypeId.removeValidators(Validators.required);
                    this.fundPledgeTypeId.updateValueAndValidity({onlySelf: true});
                }
            });
    }

    private setAlertMessages(): void {
        this.householdIncomeAlertMessage = `The client record's household income does not match the applicant's household income submitted in the public application.
This application will be validated based on what was entered on the public application - ${this.currencyPipe.transform(this.assistanceApplication?.applicantHouseHoldIncome, 'USD')}. Please update the client record's household income if necessary.
`;
        this.individualIncomeAlertMessage = `The client record's individual income does not match the applicant's individual income submitted in the public application.
This application will be validated based on what was entered on the public application - ${this.currencyPipe.transform(this.assistanceApplication?.applicantIndividualMonthlyIncome, 'USD')}. Please update the client record's individual income if necessary.`;
        this.emailAlertMessage = `The client record's email does not match the applicant's email submitted in the public application.
Please update the client record's email if necessary. Application updates will be sent to ${this.assistanceApplication?.applicantEmail}`;
        this.countyAlertMessageHtml = `The client record's county does not match the applicant's county submitted in the public application.<br>Please update the client record's address if necessary. If the application county selected is incorrect, you will have to cancel this application.<br><br>Applicant address:<br>${this.assistanceApplication?.applicantAddress}<br>${this.assistanceApplication?.applicantCity}, ${this.assistanceApplication?.applicantState} ${this.assistanceApplication?.applicantZipCode}`;
        this.countyAlertMessageText = `The client record's county does not match the applicant's county submitted in the public application.
Please update the client record's address if necessary. If the application county selected is incorrect, you will have to cancel this application.
Applicant address: ${this.assistanceApplication?.applicantAddress} ${this.assistanceApplication?.applicantCity}, ${this.assistanceApplication?.applicantState} ${this.assistanceApplication?.applicantZipCode}`;
    }

    private incomeExceedsLimit(): boolean {
        // storm application does not require income limits
        if (this.applicationType === AssistanceApplicationType.STLStorm) {
            return false;
        }

        const maxMonthlyIncome = this.getProgramMaximumHouseholdIncome();
        const monthlyIncome = this.client?.houseHoldIncome;

        return monthlyIncome > maxMonthlyIncome;
    }

    // called from parent component via view child
    public loadPayeeForm(company: any): UntypedFormGroup {
        const payeeForm = this.formBuilder.group({
            useNewCompany: [company.isLegacyPayee ?? false],
            payeeType: [company.payeeType ?? null],
            paymentTo: [''],
            payeeAddress: [company.payeeAddress || ''],
            payeeAddress2: [company.payeeAddress2 || ''],
            accountNumber: [company.accountNumber, Validators.compose([Validators.required, Validators.maxLength(100)])],
            payeeCity: [company.payeeCity || ''],
            payeeState: [company.payeeState || ''],
            payeeZip: [company.payeeZip || ''],
            id: [company.id],
            companyName: [company.companyName],
            companyId: [company.companyId],
            isLegacyPayee: [company.isLegacyPayee],
            amount: [company.amount],
            category: [company.category],
            pledgeId: [company.pledgeId || null]
        }, {
            validators: Validators.compose([
                Validators.mutuallyExclusive('companyId', 'paymentTo', 'Company', 'Payment To'),
            ])
        });
        payeeForm.get('useNewCompany').disable();
        payeeForm.get('companyName').disable();
        payeeForm.get('category').disable();
        return payeeForm;
    }

    public validateHouseHoldSize(): void {
        this.maxIncomeLevel$.next(this.getProgramMaximumHouseholdIncome());
        this.hasHouseholdMismatch$.next(this.householdSize !== this.client?.houseHoldSize);
    }

    setPayeeValidators(company: any, isPayee: boolean): void {
        if (isPayee) {
            (company)?.clearValidators();
            this.getCompanyName(company)?.removeValidators(Validators.required);
            this.getCompanyName(company)?.setValue(null);
            this.getCompanyOriginalId(company)?.setValue(null);
            this.getPayeeCategory(company)?.setValue('');
            this.getPayeePayeeType(company)?.setValidators(Validators.required);
            this.getPaymentTo(company)?.setValidators(Validators.required);
            this.getPayeeAddress(company)?.setValidators(Validators.required);
            this.getPayeeCity(company)?.setValidators(Validators.required);
            this.getPayeeState(company)?.setValidators(Validators.required);
            this.getPayeeZip(company)?.setValidators(Validators.required);
            this.getPayeeCategory(company)?.setValidators(Validators.required);
            this.getPayeeCategory(company)?.enable();
        } else {
            this.getPaymentTo(company)?.removeValidators(Validators.required);
            this.getPayeePayeeType(company)?.removeValidators(Validators.required);
            this.getCompanyName(company)?.setValidators(Validators.required);
            this.getPayeeAddress(company)?.setValidators(Validators.required);
            this.getPayeeCity(company)?.setValidators(Validators.required);
            this.getPayeeState(company)?.setValidators(Validators.required);
            this.getPayeeZip(company)?.setValidators(Validators.required);
            this.getAccountsNumber(company)?.removeValidators(Validators.required);

            const companyOriginalId = this.getCompanyOriginalId(company)?.value;
            const foundCompany = this.selectedCompanies.find(x => x.id === companyOriginalId);

            if (foundCompany) {
                this.getPayeeCategory(company)?.setValue(foundCompany.category);
                this.getPayeeCategory(company)?.disable();
            } else {
                this.getPayeeCategory(company)?.setValue('');
                this.getPayeeCategory(company)?.enable();
            }
        }

        this.getAccountsNumber(company)?.setValidators(Validators.compose([Validators.required, Validators.maxLength(100)]));

        this.getCompanyName(company)?.updateValueAndValidity({onlySelf: true});
        this.getPayeePayeeType(company)?.updateValueAndValidity({onlySelf: true});
        this.getPaymentTo(company)?.updateValueAndValidity({onlySelf: true});
        this.getPayeeAddress(company)?.updateValueAndValidity({onlySelf: true});
        this.getPayeeCity(company)?.updateValueAndValidity({onlySelf: true});
        this.getPayeeState(company)?.updateValueAndValidity({onlySelf: true});
        this.getPayeeZip(company)?.updateValueAndValidity({onlySelf: true});
        this.getAccountsNumber(company)?.updateValueAndValidity({onlySelf: true});
    }

    public hasIncome(income: number | undefined | null): boolean {
        return income !== null && income !== undefined && income >= 0;
    }

    getCompanyName(payee: AbstractControl): FormControl {
        return payee.get('companyName') as FormControl;
    }

    getCompanyOriginalId(payee: AbstractControl): FormControl {
        return payee.get('companyId') as FormControl;
    }

    getUseNewCompany(payee: AbstractControl): FormControl {
        return payee.get('useNewCompany') as FormControl;
    }

    getPayeeAmount(payee: AbstractControl): FormControl {
        return payee.get('amount') as FormControl;
    }

    getPayeeCategory(payee: AbstractControl): FormControl {
        return payee.get('category') as FormControl;
    }

    getPayeePledge(payee: AbstractControl): FormControl {
        return payee.get('pledgeId') as FormControl;
    }

    getPaymentTo(payee: AbstractControl): FormControl {
        return payee.get('paymentTo') as FormControl;
    }

    getPayeePayeeType(payee: AbstractControl): FormControl {
        return payee.get('payeeType') as FormControl;
    }

    getPayeeAddress(payee: AbstractControl): FormControl {
        return payee.get('payeeAddress') as FormControl;
    }

    getPayeeAddress2(payee: AbstractControl): FormControl {
        return payee.get('payeeAddress2') as FormControl;
    }

    getPayeeCity(payee: AbstractControl): FormControl {
        return payee.get('payeeCity') as FormControl;
    }

    getPayeeState(payee: AbstractControl): FormControl {
        return payee.get('payeeState') as FormControl;
    }

    getPayeeZip(payee: AbstractControl): FormControl {
        return payee.get('payeeZip') as FormControl;
    }

    getAccountsNumber(payee: AbstractControl): FormControl {
        return payee.get('accountNumber') as FormControl;
    }

    public populateCompanyFormGroupWithExistingCompany(i: number): void {
        const companyFormGroup = this.companiesFormArray.controls[i] as FormGroup;
        const metaData = this.companyFormArrayMetaData[i];
        if (!metaData.ignoreDuplicates && !!metaData.duplicateCompany) {
            companyFormGroup.get('useNewCompany').setValue(false);
            companyFormGroup.get('companyName').setValue(metaData.duplicateCompany.name);
            companyFormGroup.get('companyId').setValue(metaData.duplicateCompany.id);
            companyFormGroup.get('isLegacyPayee').setValue(false);
            this.getPaymentTo(companyFormGroup)?.setValue(null);
            this.getPayeeAddress(companyFormGroup)?.setValue(metaData.duplicateCompany.address1 || '');
            this.getPayeeAddress2(companyFormGroup)?.setValue(metaData.duplicateCompany.address2 || '');
            this.getPayeeCity(companyFormGroup)?.setValue(metaData.duplicateCompany.city || '');
            this.getPayeeState(companyFormGroup)?.setValue(metaData.duplicateCompany.state || '');
            this.getPayeeZip(companyFormGroup)?.setValue(metaData.duplicateCompany.zipCode || '');
        }
        this.setPayeeValidators(companyFormGroup, false);
    }

    public resetCompanyFormArrayMetaDataAtIndex(i: number): void {
        this.companyFormArrayMetaData[i] = {ignoreDuplicates: false, duplicateCompany: null};

    }

    public ignoreDuplicateCompany(i: number): void {
        this.companyFormArrayMetaData[i].ignoreDuplicates = true;
        const companyFormGroup = this.companiesFormArray.controls[i] as FormGroup;
        this.setPayeeValidators(companyFormGroup, true);
    }

    populateClient(client: Client): void {
        this.form.patchValue({
            clientId: client.id,
            clientFirstName: client.firstName,
            clientLastName: client.lastName,
            clientSsn: client.ssn,
            clientAddress: client.address1 + '\n' + client.address2,
            clientCounty: client.county,
            clientPhone: client.phone,
            clientEmail: client.email,
        });
    }

    populateUser(user: User): void {
        this.form.patchValue({assignedAgencyId: user.agencyId, createdByUserId: user.id});
    }

    showCompanyModal(company: AbstractControl): boolean {
        this.companyListModal.openModal(null, company, false);
        return false;
    }

    onCompanySelected(event: CompanySelectedEvent): void {
        if (event.company) {
            this.getCompanyName(event.payee).patchValue(event.company.name);
            this.getCompanyOriginalId(event.payee).patchValue(event.company.id);
            this.selectedCompanies.push(event.company);
            this.getPayeeCategory(event.payee)?.setValue(event.company.category);
            this.getPayeeCategory(event.payee)?.removeValidators(Validators.required);
            this.getPayeeCategory(event.payee)?.disable();
            this.getPayeeAddress(event.payee)?.setValue(event.company.address1 || '');
            this.getPayeeAddress2(event.payee)?.setValue(event.company.address2 || '');
            this.getPayeeCity(event.payee)?.setValue(event.company.city || '');
            this.getPayeeState(event.payee)?.setValue(event.company.state || '');
            this.getPayeeZip(event.payee)?.setValue(event.company.zipCode || '');
        } else {
            this.getPayeeCategory(event.payee)?.addValidators(Validators.required);
            this.getPayeeCategory(event.payee)?.enable();
        }
    }

    getHumanizedInvalidFields(): string {
        const invalidFields = [];
        Object.keys(this.form.controls).forEach(controlName => {
            if (this.form.get(controlName)?.invalid) {
                const humanizedError = this.humanizeControlName(controlName);
                invalidFields.push(humanizedError);
            }
        });

        if (this.applicationType === AssistanceApplicationType.STLStorm) {
            Object.keys(this.stormApplicationGroup.controls).forEach(controlName => {
                if (this.stormApplicationGroup.get(controlName)?.invalid) {
                    const humanizedError = this.humanizeControlName(controlName);
                    invalidFields.push(humanizedError);
                }
            });
        }

        if (this.form.errors && fileErrorNames.some(x => Object.keys(this.form.errors).includes(x))) {
            const humanizedError = this.humanizeControlName('files');
            invalidFields.push(humanizedError);
        }

        if (this.form.errors && Object.keys(this.form.errors).includes('incomeExceedsLimit')){
            invalidFields.push('Client monthly income exceeds limit');
        }

        this.formErrors$.next(invalidFields);
        return invalidFields.join('\r\n');
    }

    humanizeControlName(controlName: string): string {
        const mapping = formControlErrorMapper.find(x => x.formControlName === controlName);
        return mapping?.humanizedError;
    }

    loadFromDraft(data: InternalDraftFormData): void {
        if (!data) return;

        this.form.patchValue({
            summary: data.summary || '',
            description: data.description || '',
            totalNeed: data.totalNeed || '',
            fundPledgeTypeId: data.fundPledgeTypeId || '',
            assignedAgencyId: data.assignedAgencyId || '',
            status: data.status || AssistanceApplicationStatus.UnderReview,
            familyEducatedAboutResources: data.familyEducatedAboutResources,
        });

        if (data.areasOfNeed) {
            this.assistanceApplicationAreasOfNeedGroup.patchValue(data.areasOfNeed);
        }

        if (data.stormApplication) {
            this.stormApplicationGroup.patchValue(data.stormApplication);
        }

        // Rebuild companies FormArray
        if (data.companies?.length) {
            this.companiesFormArray.clear();
            for (const company of data.companies) {
                const companyGroup = this.initCompanyForm();
                this.companiesFormArray.push(companyGroup);
                this.companyFormArrayMetaData.push({ ignoreDuplicates: false, duplicateCompany: null });
                const isNewPayee = company.isLegacyPayee ?? false;
                companyGroup.get('useNewCompany').setValue(isNewPayee);
                this.setPayeeValidators(companyGroup, isNewPayee);
                companyGroup.patchValue(company);
            }
        }

        // Draft-loaded rows must carry the same validators as freshly-added ones
        // (createAdult/createMinor); otherwise editing a loaded draft has no
        // birth-year / required validation at all.
        const isOptional = this.isBirthYearAndSsnOptional;
        const ssnValidators = isOptional ? [] : [Validators.required];

        // Rebuild adults FormArray
        if (data.adults?.length) {
            const adultsFA = this.form.get('adults') as FormArray;
            adultsFA.clear();
            for (const adult of data.adults) {
                const adultGroup = this.formBuilder.group({
                    id: [adult.id || null],
                    firstName: [adult.firstName || '', Validators.required],
                    lastName: [adult.lastName || '', Validators.required],
                    ssn: [adult.ssn || '', Validators.compose(ssnValidators)],
                    last4Ssn: [adult.last4Ssn || ''],
                    employmentStatus: [adult.employmentStatus || '', Validators.required],
                    monthlyIncomeAmount: [adult.monthlyIncomeAmount || 0, Validators.required],
                    birthYear: [adult.birthYear || null, Validators.birthYear({required: !isOptional, kind: 'any'})]
                });
                adultsFA.push(adultGroup);
            }
        }

        // Rebuild minors FormArray
        if (data.minors?.length) {
            const minorsFA = this.form.get('minors') as FormArray;
            minorsFA.clear();
            for (const minor of data.minors) {
                const minorGroup = this.formBuilder.group({
                    id: [minor.id || null],
                    firstName: [minor.firstName || '', Validators.required],
                    lastName: [minor.lastName || '', Validators.required],
                    birthYear: [minor.birthYear || null, Validators.birthYear({required: !isOptional, kind: 'minor'})],
                    ssn: [minor.ssn || '', Validators.compose(ssnValidators)],
                    last4Ssn: [minor.last4Ssn || '']
                });
                minorsFA.push(minorGroup);
            }
        }
    }

    loadDraftFiles(files: DraftApplicationFile[]): void {
        if (!files?.length) return;

        for (const draftFile of files) {
            const fileGroup = this.formBuilder.group({
                fileType: [draftFile.fileType],
                file: [{ name: draftFile.fileName, fileUrl: draftFile.fileUrl }],
                id: [draftFile.id]
            });
            this.filesFormArray.push(fileGroup);
        }
    }

    async saveDraft(): Promise<void> {
        const isNewDraft = !this.draftId;
        this.saving$.next(true);
        this.loadingSubject.next(true);
        this.savingTitle$.next('Saving Draft');
        this.savingMessage$.next('Please wait...');
        this.setPercentComplete(0, 4);

        const formValue = this.form.getRawValue();

        const sanitize = (obj: any) => {
            if (!obj || typeof obj !== 'object') return obj;
            const result = Array.isArray(obj) ? [...obj] : {...obj};
            for (const key of Object.keys(result)) {
                if (result[key] === '') {
                    result[key] = null;
                }
            }
            return result;
        };

        const companies = this.companiesFormArray.getRawValue().map(c => {
            const sanitized = sanitize(c);
            if (sanitized) {
                sanitized.isLegacyPayee = sanitized.useNewCompany ?? false;
            }
            return sanitized;
        });

        const adults = Array.isArray(formValue.adults) ? formValue.adults.map(a => sanitize(a)) : formValue.adults;
        const minors = Array.isArray(formValue.minors) ? formValue.minors.map(m => sanitize(m)) : formValue.minors;

        const formData = InternalDraftFormData.fromJS(sanitize({
            summary: formValue.summary,
            description: formValue.description,
            note: formValue.note,
            accountNumber: formValue.accountNumber,
            totalNeed: formValue.totalNeed,
            fundPledgeTypeId: formValue.fundPledgeTypeId,
            assignedAgencyId: formValue.assignedAgencyId,
            status: formValue.status,
            familyEducatedAboutResources: formValue.familyEducatedAboutResources,
            companies,
            adults,
            minors,
            areasOfNeed: formValue.assistanceApplicationAreasOfNeed ? sanitize(formValue.assistanceApplicationAreasOfNeed) : null,
            stormApplication: this.applicationType === AssistanceApplicationType.STLStorm
                ? sanitize(formValue.stormApplication) : null,
        }));

        const request = SaveInternalDraftRequest.fromJS({
            clientId: this.client?.id,
            pledgeTypeId: formValue.fundPledgeTypeId || null,
            applicationType: this.applicationType,
            formData,
            pendingAddressChangeRequests: this.pendingAddressChangeRequests,
        });
        this.pendingAddressChangeRequests = [];

        this.setPercentComplete(1, 4);
        this.savingMessage$.next('Saving form data...');

        let draftWarnings: string[] = [];

        try {
            const result = await lastValueFrom(this.internalDraftService.save(request, this.draftId));
            this.draftId = result.id;
            draftWarnings = result.warnings ?? [];
            this.setPercentComplete(2, 4);
        } catch (err) {
            this.saving$.next(false);
            this.loadingSubject.next(false);
            this.injector.get(NotificationService).showError('Error saving draft. ' + getMessageFromException(err));
            return;
        }

        // Delete any soft-deleted draft files
        const deleteOps = this.softDeletedDraftFileIds.map(id =>
            this.draftApplicationFileClient.deleteFile(id).pipe(catchError(() => of(null)))
        );

        // Upload new local files (File instances, not previously saved draft files)
        const newFiles: { file: File, fileType: string }[] = [];
        this.filesFormArray.controls.forEach((control: any) => {
            const val = control.getRawValue();
            if (val.file instanceof File) {
                newFiles.push({ file: val.file, fileType: val.fileType });
            }
        });

        const allOps: Observable<any>[] = [...deleteOps];

        if (newFiles.length) {
            const fileParams = newFiles.map(f => ({ data: f.file, fileName: f.file.name }));
            const fileTypes = newFiles.map(f => f.fileType);
            allOps.push(
                this.draftApplicationFileClient.uploadFiles(fileTypes, this.draftId, fileParams)
            );
        }

        if (!allOps.length) {
            this.setPercentComplete(4, 4);
            setTimeout(() => {
                this.saving$.next(false);
                this.loadingSubject.next(false);
                this.injector.get(NotificationService).showSuccess('Draft saved successfully.');
                for (const warning of draftWarnings) {
                    this.injector.get(NotificationService).showError(warning);
                }
                this.navigateAfterDraftSave(isNewDraft);
            }, 500);
            return;
        }

        this.savingTitle$.next('Uploading Files');
        this.savingMessage$.next('Uploading files...');
        this.setPercentComplete(3, 4);

        try {
            const results = await lastValueFrom(forkJoin(allOps));
            this.softDeletedDraftFileIds = [];

            // Replace local File entries with metadata-only entries from upload response
            const uploadResult = newFiles.length ? results[results.length - 1] : null;
            if (uploadResult && Array.isArray(uploadResult)) {
                let uploadIdx = 0;
                this.filesFormArray.controls.forEach((control: any) => {
                    const val = control.getRawValue();
                    if (val.file instanceof File && uploadIdx < uploadResult.length) {
                        const uploaded = uploadResult[uploadIdx];
                        control.patchValue({
                            file: { name: uploaded.fileName, fileUrl: uploaded.fileUrl },
                            id: uploaded.id
                        });
                        uploadIdx++;
                    }
                });
            }

            this.setPercentComplete(4, 4);
            setTimeout(() => {
                this.saving$.next(false);
                this.loadingSubject.next(false);
                this.injector.get(NotificationService).showSuccess('Draft saved successfully.');
                for (const warning of draftWarnings) {
                    this.injector.get(NotificationService).showError(warning);
                }
                this.navigateAfterDraftSave(isNewDraft);
            }, 500);
        } catch (err) {
            this.saving$.next(false);
            this.loadingSubject.next(false);
            this.injector.get(NotificationService).showError('Error saving draft files. ' + getMessageFromException(err));
        }
    }

    private navigateAfterDraftSave(isNewDraft: boolean): void {
        if (isNewDraft) {
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { draftId: this.draftId },
            }).then(() => window.location.reload());
        } else {
            window.location.reload();
        }
    }

    async createCase(overrideSave: boolean = false): Promise<void> {
        if (!this.isValidForm()) {
            this.loadingSubject.next(false);
            this.saving$.next(false);
            this.getHumanizedInvalidFields();
            return;
        }

        this.formErrors$.next([]);

        if (!overrideSave && !this.doesCategoryAmountsHaveValidAmounts()) {
            this.showExceedsCategoryThresholdModal();
            this.loadingSubject.next(false);
            this.saving$.next(false);
            return;
        }

        this.setPercentComplete(0, 4);
        this.saving$.next(true);
        this.loadingSubject.next(true);
        this.savingTitle$.next('Creating Application');
        this.savingMessage$.next('Please wait...');
        this.form.markAllAsTouched();

        const assistanceApplicationForm = this.form.getRawValue();

        if (this.applicationType === AssistanceApplicationType.STLStorm) {
            if (!this.hasDifferentDamagedDwellingAddress) {
                assistanceApplicationForm.stormApplication.damagedDwellingAddress1 = this.client?.address1;
                assistanceApplicationForm.stormApplication.damagedDwellingAddress2 = this.client?.address2;
                assistanceApplicationForm.stormApplication.damagedDwellingCity = this.client?.city;
                assistanceApplicationForm.stormApplication.damagedDwellingState = this.client?.state;
                assistanceApplicationForm.stormApplication.damagedDwellingZipCode = this.client?.zipCode;
            }
        } else {
            assistanceApplicationForm.stormApplication = null;
        }

        const createAssistanceApplicationModel = CreateAssistanceApplication.fromJS({
            applicationType: this.applicationType,
            clientId: assistanceApplicationForm?.clientId || null,
            assignedAgencyId: this.user?.agencyId || null,
            fundPledgeTypeId: assistanceApplicationForm?.fundPledgeTypeId || null,
            totalNeed: assistanceApplicationForm?.totalNeed || null,
            summary: assistanceApplicationForm?.summary || null,
            description: assistanceApplicationForm?.description || null,
            note: assistanceApplicationForm?.note || null,
            status: assistanceApplicationForm?.status || AssistanceApplicationStatus.UnderReview,
            fundedDate: assistanceApplicationForm?.fundedDate || null,
            createdByUserId: assistanceApplicationForm?.createdByUserId || this.user?.id,
            isPublic: false,
            companies: this.companiesFormArray.getRawValue(),
            fundTypeId: this.administrativeAgencyFundTypeId,
            adults: this.sanitizeHouseholdMembers(assistanceApplicationForm?.adults),
            minors: this.sanitizeHouseholdMembers(assistanceApplicationForm?.minors),
            assistanceApplicationAreasOfNeed: assistanceApplicationForm?.assistanceApplicationAreasOfNeed,
            client: this.client,
            stormApplication: assistanceApplicationForm?.stormApplication,
            familyEducatedAboutResources: assistanceApplicationForm?.familyEducatedAboutResources,
            draftId: this.draftId || undefined,
            pendingAddressChangeRequests: this.pendingAddressChangeRequests,
        });
        this.pendingAddressChangeRequests = [];

        const localFiles: { file: { data: File, fileName: string }, type: string }[] = [];

        for (const fileWrapper of assistanceApplicationForm.files) {
            if (fileWrapper.file instanceof File) {
                localFiles.push({ file: { data: fileWrapper.file, fileName: fileWrapper.file.name }, type: fileWrapper.fileType });
            }
        }

        const filesArray = localFiles;

        let createdApplicationId: string;
        let createdApplicationWarnings: string[] = [];

        try {
            const result = await lastValueFrom(this.assistanceApplicationClient.add(createAssistanceApplicationModel));
            createdApplicationId = result.id;
            createdApplicationWarnings = result.warnings ?? [];
        } catch (err) {
            this.loadingSubject.next(false);
            this.saving$.next(false);

            if (err) {
                this.injector.get(NotificationService).showError(getMessageFromException(err));
            } else {
                this.injector.get(NotificationService)
                    .showError('An error occurred while creating your application. Please try again.');
            }
            return;
        }

        this.setPercentComplete(2, 4);

        this.uploadedCount = 0;
        this.totalToUpload = filesArray.length;

        if (this.totalToUpload > 0) {
            this.savingTitle$.next('Uploading Files');
            this.savingMessage$.next(`${0} out of ${this.totalToUpload} files uploaded - please wait...`);
        }

        const uploadRequests = filesArray.map((file: any) =>
            this.assistanceApplicationFileClient.uploadFile(createdApplicationId, file.type, file.file).pipe(
                map(result => ({ success: true as const, id: result.id })),
                tap(() => {
                    this.uploadedCount++;
                    const uploadPercentage = (this.uploadedCount / this.totalToUpload) * 75; // 75% for total process minus initial save
                    const totalPercentage = 25 + uploadPercentage;
                    this.percentComplete$.next(`${Math.floor(totalPercentage)}%`);
                    this.savingMessage$.next(`${this.uploadedCount} out of ${this.totalToUpload} files uploaded...`);
                }),
                catchError(() => of({ success: false as const, id: null }))
            )
        );

        (uploadRequests.length ? forkJoin(uploadRequests) : of([] as Array<{ success: boolean; id: string | null }>)).pipe(
            switchMap((results: Array<{ success: boolean; id: string | null }>) => {
                const failed = results.filter(r => !r.success);
                if (failed.length > 0) {
                    return this.assistanceApplicationClient.delete(createdApplicationId).pipe(
                        map(() => ({ success: false, failedCount: failed.length }))
                    );
                }
                return of({ success: true, failedCount: 0 });
            }),
            switchMap(result => {
                if (result.success && this.draftId) {
                    return this.internalDraftService.delete(this.draftId).pipe(
                        map(() => result),
                        catchError(() => of(result))
                    );
                }
                return of(result);
            })
        ).subscribe({
            next: ({ success, failedCount }) => {
                if (!success) {
                    this.loadingSubject.next(false);
                    this.saving$.next(false);
                    this.injector.get(NotificationService)
                        .showError(`An error occurred while uploading one or more of your files. The application was not submitted - please try again. If the problem persists, contact an administrator for assistance.`);
                } else {
                    this.setPercentComplete(1, 1);
                    setTimeout(() => {
                        this.loadingSubject.next(false);
                        this.saving$.next(false);
                        this.injector.get(NotificationService).showSuccess('Application Successfully Created!');
                        if (createdApplicationWarnings.length > 0) {
                            createdApplicationWarnings.forEach(warning => {
                                this.injector.get(NotificationService).showWarning(warning);
                            });
                        }
                        this.router.navigate(['../../', createdApplicationId], {relativeTo: this.activatedRoute}).then();
                    }, 500);
                }
            }
        });
    }

    addPayee(): void {
        this.companiesFormArray.push(this.initCompanyForm());
        this.companyFormArrayMetaData.push({
            ignoreDuplicates: false,
            duplicateCompany: null
        });
    }

    async overrideSave(): Promise<void> {
        if (this.isNewApplication) {
            await this.createCase(true);
        } else {
            const originalCase = this._originalEditCase.getValue();
            await this.updateCase(originalCase, true);
        }
    }

    private completeApplicationSave(skipWindowReload?: boolean): void {
        setTimeout(() => {
            this.loadingSubject.next(false);
            this.saving$.next(false);
            this.onUpdate.emit(this.assistanceApplication);
            this.injector.get(NotificationService).showSuccess('Application Successfully Updated!');
            if (this.assistanceApplication?.warnings?.length > 0) {
                for (const warning of this.assistanceApplication.warnings) {
                    this.injector.get(NotificationService).showError(warning);
                }
            }
            if (!skipWindowReload) {
                window.location.reload();
            }
        }, 500);
    }

    async updateCase(originalCase: AssistanceApplication,
                     overrideSave: boolean = false,
                     skipWindowReload?: boolean,
                     newStatus?: AssistanceApplicationStatus): Promise<boolean> {

        if (!this.isValidForm() && !(this.statusControl.value === AssistanceApplicationStatus.Denied)) {
            this.form.markAllAsTouched();
            this.loadingSubject.next(false);
            this.getHumanizedInvalidFields();
            return false;
        }

        this.formErrors$.next([]);

        this._originalEditCase.next(originalCase);

        if (!overrideSave && !this.doesCategoryAmountsHaveValidAmounts()) {
            this.showExceedsCategoryThresholdModal();
            this.loadingSubject.next(false);
            return false;
        }

        this.setPercentComplete(0, 4);
        this.saving$.next(true);
        this.loadingSubject.next(true);
        this.savingTitle$.next('Saving Application');
        this.savingMessage$.next('Please wait...');
        this.form.markAllAsTouched();
        this.form.get('assignedAgencyId').setValue(originalCase.assignedAgencyId);

        const assistanceApplicationUpdate = this.form.getRawValue();

        assistanceApplicationUpdate.assistanceApplicationAreasOfNeed.id = originalCase.assistanceApplicationAreasOfNeed.id;

        if (this.applicationType === AssistanceApplicationType.STLStorm) {
            if (!this.hasDifferentDamagedDwellingAddress) {
                assistanceApplicationUpdate.stormApplication.damagedDwellingAddress1 = this.client?.address1;
                assistanceApplicationUpdate.stormApplication.damagedDwellingAddress2 = this.client?.address2;
                assistanceApplicationUpdate.stormApplication.damagedDwellingCity = this.client?.city;
                assistanceApplicationUpdate.stormApplication.damagedDwellingState = this.client?.state;
                assistanceApplicationUpdate.stormApplication.damagedDwellingZipCode = this.client?.zipCode;
            }
        } else {
            assistanceApplicationUpdate.stormApplication = null;
        }

        const updateAssistanceApplicationModel = UpdateAssistanceApplication.fromJS({
            id: originalCase?.id,
            applicationType: originalCase.applicationType,
            clientId: assistanceApplicationUpdate?.clientId || null,
            assignedAgencyId: originalCase.assignedAgencyId || null,
            fundPledgeTypeId: assistanceApplicationUpdate?.fundPledgeTypeId || null,
            totalNeed: assistanceApplicationUpdate?.totalNeed || null,
            summary: assistanceApplicationUpdate?.summary || null,
            description: assistanceApplicationUpdate?.description || null,
            note: assistanceApplicationUpdate?.note || null,
            status: newStatus
                    ? this.assistanceApplicationStates
                    .find(x => x.key?.toLowerCase() === newStatus.toString().toLowerCase()).value
                    : this.assistanceApplicationStates
                    .find(x => x.key?.toLowerCase() === assistanceApplicationUpdate?.status?.toString()?.toLowerCase()).value
                ,
            denialReason: assistanceApplicationUpdate?.denialReason || null,
            otherDenialReason: (assistanceApplicationUpdate?.denialReason === AssistanceApplicationDenialReason.Other
                ? assistanceApplicationUpdate?.otherDenialReason : null) || null,
            fundedDate: assistanceApplicationUpdate?.fundedDate || null,
            createdByUserId: assistanceApplicationUpdate?.createdByUserId || this.user?.id,
            isPublic: false,
            companies: this.companiesFormArray.getRawValue(),
            fundTypeId: originalCase.fundTypeId,
            adults: this.sanitizeHouseholdMembers(assistanceApplicationUpdate?.adults),
            minors: this.sanitizeHouseholdMembers(assistanceApplicationUpdate?.minors),
            assistanceApplicationAreasOfNeed: assistanceApplicationUpdate?.assistanceApplicationAreasOfNeed,
            stormApplication: assistanceApplicationUpdate.stormApplication,
            familyEducatedAboutResources: assistanceApplicationUpdate?.familyEducatedAboutResources,
            pendingAddressChangeRequests: this.pendingAddressChangeRequests,
        });
        this.pendingAddressChangeRequests = [];

        this.setPercentComplete(1, 4);

        const newFilesSubmitted = this.form.get('files').value.filter((x: { id: any; }) => !x.id) as any[];
        const newFilesMapped = newFilesSubmitted.map((fileWrapper: any) => {
            return {data: fileWrapper.file, fileName: fileWrapper.file.name};
        });

        const fileTypes = newFilesSubmitted.map((fileWrapper: any) => {
            return fileWrapper.fileType;
        });

        try {
            const result = await firstValueFrom(this.assistanceApplicationClient.update(updateAssistanceApplicationModel));
            this.user = result.createdByUser;
            this.assistanceApplication = result;
            if (!this.softDeletedIds?.length && !newFilesMapped.length) {
                this.setPercentComplete(4, 4);
                this.completeApplicationSave(skipWindowReload);
                return true;
            }
            this.setPercentComplete(2, 4);
        } catch (err) {
            this.loadingSubject.next(false);
            this.saving$.next(false);
            if (err) {
                this.injector.get(NotificationService).showError(getMessageFromException(err));
            } else {
                this.injector.get(NotificationService)
                    .showError('An error occurred while updating the application. Please try again.');
            }
            return false;
        }

        this.savingTitle$.next('Updating Files');
        this.savingMessage$.next('Please wait...');

        try {

            await firstValueFrom(this.assistanceApplicationFileClient.updateFiles(
                fileTypes,
                this.softDeletedIds,
                this.assistanceApplication.id,
                newFilesMapped
            ));

            this.setPercentComplete(4, 4);
            this.completeApplicationSave(skipWindowReload);

            return true;

        } catch (err) {
            this.loadingSubject.next(false);
            this.saving$.next(false);
            this.injector.get(NotificationService)
                .showError('An error occurred while uploading one or more of your files.');
            await swal.fire({
                heightAuto: false,
                icon: 'warning',
                title: 'File Upload Error',
                text: 'There was an error uploading one or more of your files. The rest of the application was successfully updated. Please try saving again. If the error persists please contact an application administrator.',
            });
            return false;
        }
    }

    async saveDocuments(originalCase: AssistanceApplication): Promise<boolean> {
        this.form.updateValueAndValidity();
        if (this.form.invalid) {
            this.injector.get(NotificationService).showError('Please fill out all required fields and ensure all necessary documents are uploaded.');
            this.getHumanizedInvalidFields();
            return false;
        }

        const newFilesSubmitted = this.form.get('files').value.filter((x: { id: any; }) => !x.id) as any[];
        const newFilesMapped = newFilesSubmitted.map((fileWrapper: any) => {
            return {data: fileWrapper.file, fileName: fileWrapper.file.name};
        });

        const fileTypes = newFilesSubmitted.map((fileWrapper: any) => {
            return fileWrapper.fileType;
        });

        if (!this.softDeletedIds?.length && !newFilesMapped.length) {
            this.injector.get(NotificationService).showSuccess('No document changes to save.');
            return true;
        }

        this.setPercentComplete(0, 2);
        this.saving$.next(true);
        this.loadingSubject.next(true);
        this.savingTitle$.next('Saving Documents');
        this.savingMessage$.next('Please wait...');

        try {
            await firstValueFrom(this.assistanceApplicationFileClient.updateFiles(
                fileTypes,
                this.softDeletedIds,
                originalCase.id,
                newFilesMapped
            ));

            this.setPercentComplete(2, 2);
            this.completeApplicationSave();
            return true;
        } catch (err) {
            this.loadingSubject.next(false);
            this.saving$.next(false);
            this.injector.get(NotificationService)
                .showError('An error occurred while uploading one or more of your files.');
            await swal.fire({
                heightAuto: false,
                icon: 'warning',
                title: 'File Upload Error',
                text: 'There was an error uploading one or more of your files. Please try saving again. If the error persists please contact an application administrator.',
            });
            return false;
        }
    }

    removePayee(index: number): void {
        this.companiesFormArray.removeAt(index);
        this.companyFormArrayMetaData.splice(index, 1);
    }

    public getPayeeCompanyCategoryAmounts(
        utilityThresholdAvailable: boolean,
        rentalThresholdAvailable: boolean,
        transportationThresholdAvailable: boolean,
        childcareThresholdAvailable: boolean,
        carRepairThresholdAvailable: boolean
    ): IPayeeCompanyCategoryAmount[] {
        const companies = this.companiesFormArray.getRawValue();

        const payeeCompanyCategoryAmounts = new Array<IPayeeCompanyCategoryAmount>();

        companies.map(company => {
            if ((company.category === CompanyCategoryType[CompanyCategoryType.EmergencyRentalAssistance] &&
                    !rentalThresholdAvailable) ||
                (company.category === CompanyCategoryType[CompanyCategoryType.Utility] &&
                    !utilityThresholdAvailable) ||
                (company.category ===
                    CompanyCategoryType[CompanyCategoryType.TransportationForWorkforceNeeds] &&
                    !transportationThresholdAvailable) ||
                (company.category === CompanyCategoryType[CompanyCategoryType.ChildcareSupport] &&
                    !childcareThresholdAvailable) ||
                (company.category === CompanyCategoryType[CompanyCategoryType.CarRepair] &&
                    !carRepairThresholdAvailable)) {
                payeeCompanyCategoryAmounts.push({
                    paymentTo: company.paymentTo || company.companyName,
                    amount: company.amount,
                    category: company.category
                });
            }
        });

        this._companyCategoryService.setCurrentPayeeCompanyCategoryAmounts(payeeCompanyCategoryAmounts);

        return payeeCompanyCategoryAmounts;
    }

    onDeleteFile(event: string[]): void {
        this.softDeletedIds = event;
        // Track draft file deletions separately (IDs that aren't existing case file IDs)
        this.softDeletedDraftFileIds = event.filter(id =>
            id && !this.assistanceApplication?.files?.some((f: any) => f.id === id)
        );
    }

    validateAccountNumber(companyGroup: any): boolean {
        const companyOriginalId = this.getCompanyOriginalId(companyGroup)?.value;
        const foundCompany = this.selectedCompanies.find(x => x.id === companyOriginalId);
        const accountNumberField = this.getAccountsNumber(companyGroup);

        if (!foundCompany) {
            accountNumberField.setErrors(null);
            return true;
        }
        if (!foundCompany || !foundCompany.isAccountValidationEnforced) {
            accountNumberField.setErrors(null);
            return true;
        }
        const accountNumber = accountNumberField?.value;
        let regex = '';
        if (foundCompany.isAccountValidAlphabetical) {
            regex += 'a-zA-Z';
        }
        if (foundCompany.isAccountValidNumerical) {
            regex += '0-9';
        }
        if (foundCompany.isAccountValidSpecialChars) {
            regex += '\-\(\)';
        }
        const min = foundCompany.accountValidationMinLen || 0;
        const max = foundCompany.accountValidationMaxLen || 100;
        if (!(new RegExp(`^[${regex}]{${min},${max}}$`)).test(accountNumber)) {
            if ((foundCompany.accountValidationMaxLen !== null &&
                    foundCompany.accountValidationMinLen !== null &&
                    foundCompany.accountValidationMaxLen === foundCompany.accountValidationMinLen) &&
                accountNumber.length !== foundCompany.accountValidationMinLen) {
                accountNumberField.setErrors({
                    accountNumberValidation: `Account number must be ${foundCompany.accountValidationMinLen} characters. ` +
                        foundCompany.accountValidationErrorMsg
                });
                return false;
            }
            if (foundCompany.accountValidationMinLen && accountNumber.length < foundCompany.accountValidationMinLen) {
                accountNumberField.setErrors({
                    accountNumberValidation: `Account number must be at least ${foundCompany.accountValidationMinLen} characters. ` +
                        foundCompany.accountValidationErrorMsg
                });
                return false;
            }
            if (foundCompany.accountValidationMaxLen && accountNumber.length > foundCompany.accountValidationMaxLen) {
                accountNumberField.setErrors({
                    accountNumberValidation: `Account number cannot be greater than ${foundCompany.accountValidationMaxLen} characters. ` +
                        foundCompany.accountValidationErrorMsg
                });
                return false;
            }
            let allowedCharacters = '';
            if (foundCompany.isAccountValidAlphabetical && foundCompany.isAccountValidNumerical) {
                allowedCharacters = 'alphanumeric';
            } else if (foundCompany.isAccountValidAlphabetical) {
                allowedCharacters = 'alphabetic';
            } else if (foundCompany.isAccountValidNumerical) {
                allowedCharacters = 'numerical';
            }
            accountNumberField.setErrors({
                accountNumberValidation: `Account number may only contain ${allowedCharacters} characters. ` +
                    foundCompany.accountValidationErrorMsg
            });
            return false;
        }

        accountNumberField.setErrors(null);
        return true;
    }

    public openDetailAlert(text: string, html: string | HTMLElement): void {
        swal.fire({
            heightAuto: false,
            html,
            icon: 'warning',
            title: 'Warning',
            text,
        }).then();
    }

    // Storm specific methods

    private getTierMessage(): string {
        const total = this.assistanceApplication?.companies.reduce((sum, x) => sum + (x.amount || 0), 0) || 0;
        const info = this.getSelectedTierLimit();
        const usd = (n: number) => n?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

        return `The amount ${usd(total)} exceeds the ${info?.tierLabel} standard limit of ${usd(info?.limit)}.`;
    }

    /** Sums payee amounts as numbers, rounding running total to 2 decimals. */
    private getTotalPayeeAmountFromFormControls(): number {
        const fa = this.companiesFormArray;
        if (!fa) { return 0; }

        let sum = 0;
        for (const fg of fa.controls as any[]) {
            const ctrl = this.getPayeeAmount(fg);
            const n = Number(ctrl?.value ?? 0);
            if (!isFinite(n)) { continue; }
            sum = Math.round((sum + n + Number.EPSILON) * 100) / 100;
        }
        return sum;
    }

    /** Returns the standard limit and label for the current tier selection. */
    private getSelectedTierLimit(): { tierLabel: string; limit: number } | null {
        const val = this.assistanceApplication?.stormApplication?.tierLevel ?? this.tierLevelControl?.value;
        if (val === null || val === undefined) { return null; }

        const limit = this.tierStandardLimitMap.get(val);
        if (typeof limit === 'number') {
            return { tierLabel: this.getTierLabel(val), limit };
        }
        return null; // no known limit - nothing to enforce
    }

    private getTierLabel(val: any): string {
        const found = this.tierLevelOptions?.find((t: any) => t.value === val);
        if (found?.value) { return found.value; }
        const s = String(val);
        const m = /^Tier\s*([1-9]\d*)$/i.test(s) ? s.replace(/^Tier\s*/i, 'Tier ') : s.replace(/^Tier(\d)/i, 'Tier $1');
        return m;
    }

    /** Shows confirm dialog if total exceeds the selected tier limit. */
    private async checkTierExceed(): Promise<void> {
        const info = this.getSelectedTierLimit();
        if (!info) {
            // No tier/limit info
            return;
        }

        const total = this.getTotalPayeeAmountFromFormControls();

        if (total <= info.limit) {
            return;
        }

        const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        await Swal.fire({
            title: 'Amount exceeds tier limit',
            text: `The amount ${usd(total)} exceeds the ${info.tierLabel} standard limit of ${usd(info.limit)}. Please confirm to proceed.`,
            icon: 'warning',
            showCancelButton: false,
            confirmButtonText: 'Confirm',
            allowOutsideClick: false,
            allowEscapeKey: true
        });
    }

    public amountExceedsLimit(): boolean {
        const info = this.getSelectedTierLimit();
        if (!info) {
            return false;
        }

        const total = this.getTotalPayeeAmountFromFormControls();

        return total > info.limit;
    }

    formatPayeeAddress(company: AbstractControl): string {
        const parts = [
            this.getPayeeAddress(company)?.value,
            this.getPayeeAddress2(company)?.value,
            this.getPayeeCity(company)?.value,
        ].filter(p => p);

        const stateZip = [
            this.getPayeeState(company)?.value,
            this.getPayeeZip(company)?.value
        ].filter(p => p && p !== 'null').join(' ');

        if (stateZip) parts.push(stateZip);
        return parts.join(', ') || 'No address on file';
    }

    get canUpdateMasterAddress(): boolean {
        if (!this.editingAddressCompany) return false;
        const companyId = this.editingAddressCompany.get('companyId')?.value;
        const useNewCompany = this.editingAddressCompany.get('useNewCompany')?.value;
        return !!companyId && !useNewCompany;
    }

    get isSystemAdmin(): boolean {
        return this.loggedInUser?.userType === UserType.SystemAdmin;
    }

    openAddressEditModal(company: AbstractControl): void {
        this.editingAddressCompany = company;
        const companyId = company.get('companyId')?.value;
        this.updateMasterAddress = this.pendingAddressChangeRequests.some(r => r.companyId === companyId);
        this.addressSnapshot = {
            address: this.getPayeeAddress(company)?.value,
            address2: this.getPayeeAddress2(company)?.value,
            city: this.getPayeeCity(company)?.value,
            state: this.getPayeeState(company)?.value,
            zip: this.getPayeeZip(company)?.value,
        };
        this.addressEditModal.modal.open();
    }

    saveAddressEdit(): void {
        const companyId = this.editingAddressCompany?.get('companyId')?.value;

        if (this.updateMasterAddress && this.editingAddressCompany && companyId) {
            const request = new CompanyAddressChangeRequestViewModel();
            request.companyId = companyId;
            request.requestedByUserId = this.loggedInUser?.id;
            request.address1 = this.getPayeeAddress(this.editingAddressCompany)?.value;
            request.address2 = this.getPayeeAddress2(this.editingAddressCompany)?.value;
            request.city = this.getPayeeCity(this.editingAddressCompany)?.value;
            request.state = this.getPayeeState(this.editingAddressCompany)?.value;
            request.zipCode = this.getPayeeZip(this.editingAddressCompany)?.value;

            // Replace any existing pending request for the same company
            const existingIndex = this.pendingAddressChangeRequests.findIndex(r => r.companyId === companyId);
            if (existingIndex >= 0) {
                this.pendingAddressChangeRequests[existingIndex] = request;
            } else {
                this.pendingAddressChangeRequests.push(request);
            }
        } else if (companyId) {
            // Checkbox was unchecked — remove any pending request for this company
            this.pendingAddressChangeRequests = this.pendingAddressChangeRequests.filter(r => r.companyId !== companyId);
        }
        this.addressEditModal.modal.close();
        this.editingAddressCompany = null;
        this.addressSnapshot = null;
    }

    cancelAddressEdit(): void {
        if (this.editingAddressCompany && this.addressSnapshot) {
            this.getPayeeAddress(this.editingAddressCompany)?.setValue(this.addressSnapshot.address);
            this.getPayeeAddress2(this.editingAddressCompany)?.setValue(this.addressSnapshot.address2);
            this.getPayeeCity(this.editingAddressCompany)?.setValue(this.addressSnapshot.city);
            this.getPayeeState(this.editingAddressCompany)?.setValue(this.addressSnapshot.state);
            this.getPayeeZip(this.editingAddressCompany)?.setValue(this.addressSnapshot.zip);
        }
        this.updateMasterAddress = false;
        this.addressEditModal.modal.close();
        this.editingAddressCompany = null;
        this.addressSnapshot = null;
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.saving$.getValue()) {
            event.preventDefault();
            event.returnValue = '';
        }
    }
}
